import { GAME } from '@continuum/show-core';
import type { FixPuzzle } from '@continuum/show-core';
import type { Lang } from './text.js';

/**
 * The words of FIX (Marek, 2026-09-29): an alarm now starts a ten-second countdown on the called
 * phones, then each gets a short puzzle, and all but one of them solving it fixes the alarm. Plain
 * words, one idea a line, English and bokmål with the same keys (the type holds them to it).
 */
export interface FixCopy {
  missionIn: (seconds: number) => string;
  /** Under the countdown: who is on it and what is coming. */
  missionWho: (others: readonly string[]) => string;
  getReady: string;
  /** "1 of 2 fixed": only how many, never who. */
  count: (fixes: number, need: number) => string;
  left: (clock: string) => string;
  solved: string;
  solvedWait: string;
  votedOut: string;
  wrong: string;
  hide: string;
  /** The bar at the top, for a guest handed FIX. */
  barSoon: (seconds: number) => string;
  barSolve: string;
  barDone: string;
  /** Everyone else's bar and card. */
  othersSoon: (seconds: number) => string;
  othersSolving: (names: readonly string[]) => string;
  puzzleTitle: Record<FixPuzzle, string>;
  puzzleHow: Record<FixPuzzle, string>;
  remember: string;
  typeIt: string;
  tapInOrder: string;
  start: string;
  finish: string;
  timeUp: string;
  tryNext: string;
  /** The practice page's header over a FIX puzzle. */
  missionPractice: string;
  missionPracticeHow: string;
}

function andList(names: readonly string[], and: string): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;
}

const secs = Math.round(GAME.FIX_COUNTDOWN_MS / 1000);

const EN: FixCopy = {
  missionIn: (n) => `FIX MISSION IN ${n}`,
  missionWho: (others) =>
    others.length > 0 ? `An alarm. You and ${andList(others, 'and')} are fixing it.` : 'An alarm. You are fixing it.',
  getReady: `A quick puzzle is coming in ${secs} seconds. Get ready.`,
  count: (fixes, need) => `${fixes} of ${need} fixed`,
  left: (clock) => `${clock} left`,
  solved: 'SOLVED ✓',
  solvedWait: 'Your part is done. Waiting for the others to solve theirs.',
  votedOut: 'You are voted out, so you can’t fix this one.',
  wrong: 'Not quite. Watch it again.',
  hide: 'HIDE',
  barSoon: (n) => `FIX mission in ${n} — tap to open`,
  barSolve: 'Your FIX puzzle is waiting — tap to solve it',
  barDone: 'Solved — waiting for the others',
  othersSoon: (n) => `FIX mission in ${n}`,
  othersSolving: (names) => `${andList(names, 'and')} must solve FIX — hurry them!`,
  puzzleTitle: { circuit: 'RECONNECT THE CIRCUIT', code: 'REMEMBER THE CODE', order: 'PUT THEM BACK IN ORDER' },
  puzzleHow: {
    circuit: 'Tap the tiles to turn them until the line runs from START to END.',
    code: 'A four-digit code shows for 5 seconds. Then type it.',
    order: 'Four symbols show in order for 5 seconds. Then tap them in that order.',
  },
  remember: 'Remember it',
  typeIt: 'Now type the code',
  tapInOrder: 'Now tap them in the same order',
  start: 'START',
  finish: 'END',
  timeUp: 'Time is up.',
  tryNext: 'NEXT',
  missionPractice: 'FIX MISSION',
  missionPracticeHow: 'As a guest handed FIX gets it: a countdown, then 40 seconds.',
};

const NO: FixCopy = {
  missionIn: (n) => `FIKSEOPPDRAG OM ${n}`,
  missionWho: (others) =>
    others.length > 0 ? `En alarm. Du og ${andList(others, 'og')} skal fikse den.` : 'En alarm. Du skal fikse den.',
  getReady: `Et kort puslespill kommer om ${secs} sekunder. Gjør deg klar.`,
  count: (fixes, need) => `${fixes} av ${need} fikset`,
  left: (clock) => `${clock} igjen`,
  solved: 'LØST ✓',
  solvedWait: 'Din del er gjort. Venter på at de andre løser sine.',
  votedOut: 'Du er stemt ut, så du kan ikke fikse denne.',
  wrong: 'Ikke helt. Se igjen.',
  hide: 'SKJUL',
  barSoon: (n) => `Fikseoppdrag om ${n} – trykk for å åpne`,
  barSolve: 'Fikse-puslespillet ditt venter – trykk for å løse det',
  barDone: 'Løst – venter på de andre',
  othersSoon: (n) => `Fikseoppdrag om ${n}`,
  othersSolving: (names) => `${andList(names, 'og')} må løse FIKS – mas på dem!`,
  puzzleTitle: { circuit: 'KOBLE SAMMEN KRETSEN', code: 'HUSK KODEN', order: 'LEGG DEM I RIKTIG REKKEFØLGE' },
  puzzleHow: {
    circuit: 'Trykk på brikkene for å snu dem til linjen går fra START til SLUTT.',
    code: 'En firesifret kode vises i 5 sekunder. Så skriver du den inn.',
    order: 'Fire symboler vises i rekkefølge i 5 sekunder. Så trykker du dem i samme rekkefølge.',
  },
  remember: 'Husk den',
  typeIt: 'Skriv inn koden nå',
  tapInOrder: 'Trykk dem i samme rekkefølge nå',
  start: 'START',
  finish: 'SLUTT',
  timeUp: 'Tiden er ute.',
  tryNext: 'NESTE',
  missionPractice: 'FIKSEOPPDRAG',
  missionPracticeHow: 'Slik en gjest med FIKS får det: en nedtelling, så 40 sekunder.',
};

export const FIX_COPY: Record<Lang, FixCopy> = { en: EN, no: NO };
